import React, { type FC, Fragment, useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import HttpStatus from 'app/components/HttpStatus';
import { SsrModule } from 'app/constants/ssrModules';
import { withinBettingContext } from 'app/contexts/betting';
import type { SportId } from 'app/lib/betting/constants';
import { setMatchesContainerHeight } from 'app/modules/Prematch/actions';
import {
  getMapMatchIdsFilteredByTimeFilter,
} from 'app/redux/betting-presentation/grid/utils/get-map-match-ids-filtered-by-time-filter';
import { timeFilterValueProcessing } from 'app/redux/betting-presentation/grid/utils/time-filter-value-processing';
import EmptyGame from 'app/widgets/EmptyGame';

import GridHeader from '../GridHeader';
import MatchesContainer from '../MatchesContainer';
import MatchTable from '../MatchTable';
import GridStageName from './StageName';

type StoreProps = {
  sportId: SportId;
  matchIdsByStageId: Map<number, Set<number>>;
};

type BettingStagesGridProps = {
  timeFilterValue?: string;
};

const BettingStagesGrid: FC<BettingStagesGridProps & StoreProps> = ({
  sportId,
  matchIdsByStageId,
}) => {
  const dispatch = useDispatch();
  const matchesContainerRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    return () => {
      dispatch(setMatchesContainerHeight(matchesContainerRef.current?.clientHeight || null));
    };
  }, [matchesContainerRef.current]);

  if (matchIdsByStageId.size === 0) {
    return (
      <HttpStatus code={404}>
        <EmptyGame typeText="noFilteredEvents" />
      </HttpStatus>
    );
  }

  const tournamentStageIds = [...matchIdsByStageId.keys()];

  return (
    <div data-ssr-id={SsrModule.STAGE_TABLE}>
      <GridHeader shouldBeLink
                  sportId={sportId} />

      <MatchesContainer ref={matchesContainerRef}>
        {tournamentStageIds.map((tournamentStageId: number) => (
          <Fragment key={tournamentStageId}>
            <GridStageName sportId={sportId}
                           tournamentStageId={tournamentStageId} />
            <MatchTable sportId={sportId}
                        tournamentStageId={tournamentStageId}
                        matchIds={matchIdsByStageId.get(tournamentStageId)} />
          </Fragment>
        ))}
      </MatchesContainer>
    </div>
  );
};

const createMapStateToProps = (state: ReduxState, {
  $betting: {
    navigation: {
      selectors: {
        currentLocation,
      },
    },
    grid: {
      selectors: {
        stageGridSelector,
        selectFilteredMatchesByTimeFilter,
      },
    },
  },
  timeFilterValue,
}: BettingContextProps): StoreProps => {
  const { sportId, tournamentId } = currentLocation(state);
  const matchIdsByStageId = stageGridSelector(state, tournamentId);

  if (!timeFilterValue || timeFilterValue === 'all') {
    return {
      sportId,
      matchIdsByStageId,
    };
  }

  const {
    timeFilterValueNumber,
    isValidTimeFilterValue,
  } = timeFilterValueProcessing(timeFilterValue);

  if (!isValidTimeFilterValue) {
    return {
      sportId,
      matchIdsByStageId,
    };
  }

  const filteredMatches = selectFilteredMatchesByTimeFilter(state, timeFilterValueNumber)[sportId];

  if (!filteredMatches) {
    return {
      sportId,
      matchIdsByStageId: new Map(),
    };
  }

  const tournamentMatchIdsFilteredByTimeFilter = getMapMatchIdsFilteredByTimeFilter({
    filteredMatches,
    mapMatchIdsByStageIds: matchIdsByStageId,
  });

  return {
    sportId,
    matchIdsByStageId: tournamentMatchIdsFilteredByTimeFilter,
  };
};

export default compose<FC<BettingStagesGridProps>>(
  withinBettingContext,
  connect(createMapStateToProps),
)(BettingStagesGrid);
